import React, { createContext, useEffect, useState } from 'react';
import { signMessage } from '@wagmi/core';

import userService from 'services/user';

import wagmiConfig from 'wagmiConfig';
import { AUTH_MESSAGE_TO_SIGN, SIGNATURE_EXPIRY_TIME } from 'constants/index';
import useWalletContext from 'hooks/useWalletContext';
import useNotification from 'hooks/useNotification';

export const TokenContext = createContext(null);

const WALLET_TOKENS_KEY = 'wallet_tokens';
const ACTIVE_WALLET_KEY = 'active_wallet_address';

const isBrowser = () => typeof window !== 'undefined';

const readWalletTokens = () => {
  if (!isBrowser()) {
    return {};
  }

  const raw = localStorage.getItem(WALLET_TOKENS_KEY);

  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch (err) {
    console.warn('Failed to parse wallet tokens store', err);
    return {};
  }
};

const writeWalletTokens = (tokensMap) => {
  if (!isBrowser()) {
    return;
  }

  const keys = Object.keys(tokensMap || {});
  if (!keys.length) {
    localStorage.removeItem(WALLET_TOKENS_KEY);
    return;
  }

  localStorage.setItem(WALLET_TOKENS_KEY, JSON.stringify(tokensMap));
};

const getWalletTokenEntry = (address) => {
  if (!address) {
    return null;
  }

  const tokens = readWalletTokens();
  return tokens[address.toLowerCase()] || null;
};

const setActiveWallet = (address) => {
  if (!isBrowser()) {
    return;
  }

  if (!address) {
    localStorage.removeItem(ACTIVE_WALLET_KEY);
    return;
  }

  localStorage.setItem(ACTIVE_WALLET_KEY, address.toLowerCase());
};

const upsertWalletTokenEntry = (address, token, expiresAt) => {
  if (!isBrowser() || !address || !token || !expiresAt) {
    return;
  }

  const lowerAddress = address.toLowerCase();
  const tokens = readWalletTokens();

  tokens[lowerAddress] = {
    token,
    expiresAt,
    lastUsed: Date.now(),
  };

  writeWalletTokens(tokens);
  setActiveWallet(lowerAddress);
};

const updateWalletTokenUsage = (address) => {
  if (!isBrowser() || !address) {
    return;
  }

  const lowerAddress = address.toLowerCase();
  const tokens = readWalletTokens();

  if (!tokens[lowerAddress]) {
    return;
  }

  tokens[lowerAddress] = {
    ...tokens[lowerAddress],
    lastUsed: Date.now(),
  };

  writeWalletTokens(tokens);
  setActiveWallet(lowerAddress);
};

const removeWalletTokenEntry = (address) => {
  if (!isBrowser() || !address) {
    return;
  }

  const lowerAddress = address.toLowerCase();
  const tokens = readWalletTokens();

  if (!tokens[lowerAddress]) {
    return;
  }

  delete tokens[lowerAddress];
  writeWalletTokens(tokens);

  if (localStorage.getItem(ACTIVE_WALLET_KEY) === lowerAddress) {
    clearActiveWallet();
  }
};

const clearActiveWallet = () => {
  setActiveWallet(null);
};

const clearAuthStorage = () => {
  if (!isBrowser()) {
    return;
  }

  localStorage.removeItem('token');
  localStorage.removeItem('token_expiry');
};

const decodeJwtPayload = (token) => {
  if (!isBrowser() || !token) {
    return null;
  }

  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }

    return JSON.parse(window.atob(payload));
  } catch (err) {
    console.warn('Failed to decode token payload', err);
    return null;
  }
};

export const TokenProvider = ({ children }) => {
  const { disconnect, address, isConnected } = useWalletContext();
  const showNotification = useNotification();

  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setAuthenticated] = useState();

  const signOut = async (isTokenInvalid = false) => {
    setLoading(true);
    if (localStorage.token) {
      if (!isTokenInvalid) {
        await userService.logout();
      }

      clearAuthStorage();
    }

    if (address) {
      removeWalletTokenEntry(address);
    } else {
      const fallbackAddress = localStorage.getItem(ACTIVE_WALLET_KEY);
      if (fallbackAddress) {
        removeWalletTokenEntry(fallbackAddress);
      }
    }

    clearActiveWallet();
    disconnect();
    setLoading(false);
  };

  const signIn = async () => {
    setLoading(true);
    try {
      const nonceData = await userService.getNonce(address);

      if (nonceData?.message === 'Account locked!') {
        return showNotification({ type: 'error', message: nonceData?.message });
      }

      const signResult = await signMessage(wagmiConfig, {
        message: AUTH_MESSAGE_TO_SIGN(address, nonceData.data.nonce),
      });

      const authResponseData = await userService.authenticate(address, signResult);
      if (authResponseData) {
        const { token } = authResponseData.data.auth;
        localStorage.setItem('token', token);
        const expiresAt = Date.now() + SIGNATURE_EXPIRY_TIME;
        localStorage.setItem('token_expiry', expiresAt);
        upsertWalletTokenEntry(address, token, expiresAt);

        setAuthenticated(true);
        showNotification({ type: 'success', message: 'Wallet connected successfully' });
      }
    } catch (error) {
      showNotification({ type: 'error', message: 'Failed to connect wallet' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const tokenExpiry = localStorage.getItem('token_expiry');
    const normalizedAddress = address?.toLowerCase();

    if (!isConnected || !normalizedAddress) {
      if (token || tokenExpiry) {
        clearAuthStorage();
      }
      clearActiveWallet();
      setAuthenticated(false);
      return;
    }

    const entry = getWalletTokenEntry(normalizedAddress);

    if (entry && entry.token && entry.expiresAt > Date.now()) {
      if (token !== entry.token) {
        localStorage.setItem('token', entry.token);
      }
      if (tokenExpiry !== String(entry.expiresAt)) {
        localStorage.setItem('token_expiry', entry.expiresAt);
      }

      updateWalletTokenUsage(normalizedAddress);
      setAuthenticated(true);
      return;
    }

    if (entry && entry.expiresAt <= Date.now()) {
      removeWalletTokenEntry(normalizedAddress);
    }

    if (token) {
      try {
        const payload = decodeJwtPayload(token);
        const derivedAddress = payload?.address?.toLowerCase();
        const derivedExpiry = tokenExpiry ? parseInt(tokenExpiry, 10) : null;

        if (
          derivedAddress === normalizedAddress &&
          derivedExpiry &&
          Number.isFinite(derivedExpiry) &&
          derivedExpiry > Date.now()
        ) {
          upsertWalletTokenEntry(normalizedAddress, token, derivedExpiry);
          updateWalletTokenUsage(normalizedAddress);
          setAuthenticated(true);
          return;
        }
      } catch (error) {
        console.warn('Failed to restore session from existing token', error);
      }
    }

    clearAuthStorage();
    clearActiveWallet();

    setAuthenticated(false);
  }, [address, isConnected]);

  return (
    <TokenContext.Provider
      value={{
        isAuthenticated,
        connectedAndAuth: isConnected && isAuthenticated,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};
